import { useEffect, useMemo, useState } from "react";
import {
  ImageIcon,
  Images,
  Info,
  Loader2,
  Palette,
  Plus,
  Save,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import {
  createUserBrand,
  deleteUserBrand,
  getUserBrands,
  updateUserBrand,
  type PaletteSource,
  type UserBrand,
  type UserBrandPayload,
} from "../../services/settings/userBrands";
import { Language } from "../../utils/dictionary";
import { fileToPngDataUrl } from "../../utils/imageDataUrl";

type BrandColorKey =
  | "primary_color"
  | "light_background_color"
  | "dark_background_color"
  | "saturated_background_color"
  | "accent_color"
  | "complementary_color";

type BrandFormState = {
  id?: string;
  name: string;
  brandboard_url: string;
  brandboard_image: string;
  logo_url: string;
  logo_image: string;
  logo_dark_url: string;
  logo_dark_image: string;
  logo_light_url: string;
  logo_light_image: string;
  source_images: string[];
  primary_color: string;
  light_background_color: string;
  dark_background_color: string;
  saturated_background_color: string;
  accent_color: string;
  complementary_color: string;
  font_family: string;
  font_technical_description: string;
  personality_text: string;
  palette_source: PaletteSource;
};

const emptyForm: BrandFormState = {
  name: "",
  brandboard_url: "",
  brandboard_image: "",
  logo_url: "",
  logo_image: "",
  logo_dark_url: "",
  logo_dark_image: "",
  logo_light_url: "",
  logo_light_image: "",
  source_images: [],
  primary_color: "#111827",
  light_background_color: "#f8fafc",
  dark_background_color: "#111827",
  saturated_background_color: "#1f2937",
  accent_color: "#2563eb",
  complementary_color: "#f97316",
  font_family: "",
  font_technical_description: "",
  personality_text: "",
  palette_source: "auto",
};

const copy = {
  title: {
    [Language.SPANISH]: "Marcas",
    [Language.ENGLISH]: "Brands",
    [Language.PORTUGUESE]: "Marcas",
  },
  empty: {
    [Language.SPANISH]: "Aun no tienes marcas guardadas.",
    [Language.ENGLISH]: "You do not have saved brands yet.",
    [Language.PORTUGUESE]: "Voce ainda nao tem marcas salvas.",
  },
  add: {
    [Language.SPANISH]: "Nueva marca",
    [Language.ENGLISH]: "New brand",
    [Language.PORTUGUESE]: "Nova marca",
  },
  edit: {
    [Language.SPANISH]: "Editar marca",
    [Language.ENGLISH]: "Edit brand",
    [Language.PORTUGUESE]: "Editar marca",
  },
  save: {
    [Language.SPANISH]: "Guardar",
    [Language.ENGLISH]: "Save",
    [Language.PORTUGUESE]: "Salvar",
  },
  paletteSourceTitle: {
    [Language.SPANISH]: "De donde salen los colores",
    [Language.ENGLISH]: "Where colors come from",
    [Language.PORTUGUESE]: "De onde vem as cores",
  },
  paletteSourceAuto: {
    [Language.SPANISH]: "Automatico",
    [Language.ENGLISH]: "Automatic",
    [Language.PORTUGUESE]: "Automatico",
  },
  paletteSourceLogo: {
    [Language.SPANISH]: "De mi logo",
    [Language.ENGLISH]: "From my logo",
    [Language.PORTUGUESE]: "Do meu logo",
  },
  paletteSourcePackaging: {
    [Language.SPANISH]: "Del empaque",
    [Language.ENGLISH]: "From packaging",
    [Language.PORTUGUESE]: "Da embalagem",
  },
};

const paletteSourceOptions: Array<{ value: PaletteSource; labelKey: keyof typeof copy }> = [
  { value: "auto", labelKey: "paletteSourceAuto" },
  { value: "logo", labelKey: "paletteSourceLogo" },
  { value: "packaging", labelKey: "paletteSourcePackaging" },
];

const colorFields: Array<{
  key: BrandColorKey;
  label: string;
  help: string;
}> = [
  {
    key: "primary_color",
    label: "Color principal",
    help: "Tono que representa la marca o el producto. Si el producto es negro/blanco con detalles de color, usa esos detalles.",
  },
  {
    key: "light_background_color",
    label: "Color claro",
    help: "Fondo claro y elementos suaves. Debe ser una variacion clara del tono principal y conservar contraste.",
  },
  {
    key: "dark_background_color",
    label: "Color oscuro",
    help: "Fondos oscuros y elementos de peso visual. Debe contrastar bien con texto y tonos claros.",
  },
  {
    key: "saturated_background_color",
    label: "Color saturado",
    help: "Version intensa para bloques graficos, fondos de impacto o piezas donde la marca necesita mas energia.",
  },
  {
    key: "accent_color",
    label: "Color de acento",
    help: "Contraste principal para botones, llamadas de accion y detalles destacados.",
  },
  {
    key: "complementary_color",
    label: "Color complementario",
    help: "Color opcional que completa la paleta y ayuda a variar piezas sin perder coherencia.",
  },
];

const cleanColor = (value: unknown, fallback: string) => {
  const text = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(text) ? text : fallback;
};

const pickColor = (colors: Record<string, any>, fallback: string, ...keys: string[]) =>
  cleanColor(keys.map((key) => colors[key]).find(Boolean), fallback);

const brandToForm = (brand?: UserBrand): BrandFormState => {
  if (!brand) return { ...emptyForm };
  const colors = brand.brand_colors || {};
  return {
    ...emptyForm,
    id: brand.id,
    name: brand.name || "",
    brandboard_url: brand.brandboard_url || "",
    logo_url: brand.logo_url || "",
    logo_dark_url: brand.logo_dark_url || "",
    logo_light_url: brand.logo_light_url || "",
    primary_color: pickColor(colors, emptyForm.primary_color, "primary_color", "primary", "main_color"),
    light_background_color: pickColor(
      colors,
      emptyForm.light_background_color,
      "light_background_color",
      "secondary",
      "light"
    ),
    dark_background_color: pickColor(colors, emptyForm.dark_background_color, "dark_background_color", "dark"),
    saturated_background_color: pickColor(
      colors,
      emptyForm.saturated_background_color,
      "saturated_background_color",
      "saturated"
    ),
    accent_color: pickColor(colors, emptyForm.accent_color, "accent_color", "accent"),
    complementary_color: pickColor(colors, emptyForm.complementary_color, "complementary_color", "complementary"),
    font_family: brand.font_family || "",
    font_technical_description: brand.font_technical_description || "",
    personality_text: brand.personality_text || "",
  };
};

const formToPayload = (form: BrandFormState): UserBrandPayload => {
  const hasVisualInput = Boolean(
    form.source_images.length > 0 ||
    form.logo_image ||
    form.logo_url.trim() ||
    form.logo_dark_image ||
    form.logo_dark_url.trim() ||
    form.logo_light_image ||
    form.logo_light_url.trim() ||
    form.brandboard_image ||
    form.brandboard_url.trim()
  );

  return {
    name: form.name.trim() || "Marca nueva",
    brandboard_url: form.brandboard_url.trim() || null,
    brandboard_image: form.brandboard_image || null,
    logo_url: form.logo_url.trim() || null,
    logo_image: form.logo_image || null,
    logo_dark_url: form.logo_dark_url.trim() || null,
    logo_dark_image: form.logo_dark_image || null,
    logo_light_url: form.logo_light_url.trim() || null,
    logo_light_image: form.logo_light_image || null,
    source_images: form.source_images.length > 0 ? form.source_images : null,
    brand_colors: colorFields.reduce<Record<string, string>>((acc, field) => {
      acc[field.key] = form[field.key];
      return acc;
    }, {}),
    font_family: form.font_family.trim() || null,
    font_technical_description: form.font_technical_description.trim() || null,
    personality_text: form.personality_text.trim() || null,
    auto_generate_metadata: !form.id && hasVisualInput,
    palette_source: form.palette_source,
  };
};

const cardSwatches = (colors: Record<string, any>) =>
  [
    colors.primary_color || colors.primary,
    colors.light_background_color || colors.secondary,
    colors.dark_background_color || colors.dark,
    colors.saturated_background_color,
    colors.accent_color || colors.accent,
    colors.complementary_color || colors.complementary,
  ].filter(Boolean);

const FieldTitle = ({ label, help }: { label: string; help: string }) => (
  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
    {label}
    <span title={help} aria-label={help}>
      <Info className="h-3.5 w-3.5 text-gray-400" />
    </span>
  </span>
);

export function BrandsSettingsPanel() {
  const { language } = useAuth();
  const [brands, setBrands] = useState<UserBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<BrandFormState | null>(null);

  const previewBrandboard = useMemo(
    () => form?.brandboard_image || form?.brandboard_url || "",
    [form]
  );
  const previewLogo = useMemo(() => form?.logo_image || form?.logo_url || "", [form]);
  const previewDarkLogo = useMemo(() => form?.logo_dark_image || form?.logo_dark_url || previewLogo, [form, previewLogo]);
  const previewLightLogo = useMemo(() => form?.logo_light_image || form?.logo_light_url || previewLogo, [form, previewLogo]);
  const canSaveForm = Boolean(
    form &&
      (
        form.name.trim() ||
        form.personality_text.trim() ||
        form.source_images.length > 0 ||
        form.logo_image ||
        form.logo_url.trim() ||
        form.logo_dark_image ||
        form.logo_dark_url.trim() ||
        form.logo_light_image ||
        form.logo_light_url.trim() ||
        form.brandboard_image ||
        form.brandboard_url.trim()
      )
  );

  const loadBrands = async () => {
    setLoading(true);
    setError(null);
    try {
      setBrands(await getUserBrands());
    } catch (err) {
      console.error("Error loading brands", err);
      setError("No pudimos cargar tus marcas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBrands();
  }, []);

  const handleLogoFile = async (file?: File) => {
    if (!file || !form) return;
    try {
      const png = await fileToPngDataUrl(file);
      setForm({ ...form, logo_image: png, logo_url: "" });
    } catch (err) {
      console.error("Error processing logo", err);
      setError("No pudimos procesar el logo.");
    }
  };

  const handleLogoVariantFile = async (variant: "dark" | "light", file?: File) => {
    if (!file || !form) return;
    try {
      const png = await fileToPngDataUrl(file);
      setForm(
        variant === "dark"
          ? { ...form, logo_dark_image: png, logo_dark_url: "" }
          : { ...form, logo_light_image: png, logo_light_url: "" }
      );
    } catch (err) {
      console.error("Error processing logo variant", err);
      setError("No pudimos procesar la version del logo.");
    }
  };

  const handleBrandboardFile = async (file?: File) => {
    if (!file || !form) return;
    try {
      const png = await fileToPngDataUrl(file);
      setForm({ ...form, brandboard_image: png, brandboard_url: "" });
    } catch (err) {
      console.error("Error processing brandboard", err);
      setError("No pudimos procesar el brandboard.");
    }
  };

  const handleSourceFiles = async (files?: FileList | null) => {
    if (!files || !form) return;
    try {
      const selected = Array.from(files).slice(0, 5);
      const pngs = await Promise.all(selected.map((file) => fileToPngDataUrl(file)));
      setForm({ ...form, source_images: pngs });
    } catch (err) {
      console.error("Error processing brand images", err);
      setError("No pudimos procesar las imagenes de marca.");
    }
  };

  const handleSave = async () => {
    if (!form || !canSaveForm) return;
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        await updateUserBrand(form.id, formToPayload(form));
      } else {
        await createUserBrand(formToPayload(form));
      }
      setForm(null);
      await loadBrands();
    } catch (err) {
      console.error("Error saving brand", err);
      setError("No pudimos guardar la marca.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (brand: UserBrand) => {
    setSaving(true);
    setError(null);
    try {
      await deleteUserBrand(brand.id);
      setBrands((items) => items.filter((item) => item.id !== brand.id));
      setForm(null);
    } catch (err) {
      console.error("Error deleting brand", err);
      setError("No pudimos eliminar la marca.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="mb-5 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
          {copy.title[language]}
        </h1>
        <button
          type="button"
          onClick={() => setForm({ ...emptyForm, source_images: [] })}
          className="inline-flex h-10 items-center gap-2 rounded-xl bg-gray-950 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
        >
          <Plus className="h-4 w-4" />
          {copy.add[language]}
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex min-h-40 items-center justify-center text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : brands.length === 0 ? (
        <button
          type="button"
          onClick={() => setForm({ ...emptyForm, source_images: [] })}
          className="flex min-h-40 w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-white text-gray-500"
        >
          <Palette className="h-6 w-6" />
          <span className="text-sm font-medium">{copy.empty[language]}</span>
        </button>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {brands.map((brand) => {
            const colors = brand.brand_colors || {};
            const cardImage = brand.brandboard_url || brand.logo_url || "";
            return (
              <button
                type="button"
                key={brand.id}
                onClick={() => setForm(brandToForm(brand))}
                className="group overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md"
              >
                <div className="relative flex aspect-[4/5] items-center justify-center bg-gray-50 p-2">
                  {cardImage ? (
                    <img src={cardImage} alt={brand.name} className="h-full w-full rounded-lg object-contain" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-gray-300" />
                  )}
                  <span className="absolute right-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200">
                    Abrir
                  </span>
                </div>
                <div className="space-y-2 p-3">
                  <p className="truncate text-sm font-semibold text-gray-950">{brand.name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cardSwatches(colors).map((color) => (
                      <span
                        key={String(color)}
                        className="h-4 w-4 rounded-full ring-1 ring-gray-200"
                        style={{ backgroundColor: String(color) }}
                      />
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/30 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-950">{form.id ? form.name : copy.add[language]}</h2>
                <p className="text-sm text-gray-500">
                  Sube imagenes y una descripcion. Fluxi crea el brandboard, extrae tonos y prepara logos para fondos claros y oscuros.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForm(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-4">
                <div>
                  <FieldTitle
                    label="Brandboard"
                    help="Imagen vertical principal de la marca. Es la pieza que se ve en las tarjetas y resume logo, paleta, tipografia y contexto visual."
                  />
                  <div className="mt-2 flex aspect-[4/5] items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-2">
                    {previewBrandboard ? (
                      <img src={previewBrandboard} alt={form.name || "Brandboard"} className="h-full w-full rounded-lg object-contain" />
                    ) : (
                      <Images className="h-9 w-9 text-gray-300" />
                    )}
                  </div>
                  <label className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gray-50 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100">
                    <Upload className="h-4 w-4" />
                    Brandboard PNG
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => void handleBrandboardFile(event.target.files?.[0])}
                    />
                  </label>
                </div>

                <div>
                  <FieldTitle
                    label="Logo limpio"
                    help="Imagen aislada del logo. Si existe, se usa como referencia visible para portada y oferta sin recortar ni deformar."
                  />
                  <div className="mt-2 flex h-24 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 p-3">
                    {previewLogo ? (
                      <img src={previewLogo} alt={form.name || "Logo"} className="max-h-full max-w-full object-contain" />
                    ) : (
                      <ImageIcon className="h-7 w-7 text-gray-300" />
                    )}
                  </div>
                  <label className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gray-50 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100">
                    <Upload className="h-4 w-4" />
                    Logo PNG
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => void handleLogoFile(event.target.files?.[0])}
                    />
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  <div>
                    <FieldTitle
                      label="Logo negro"
                      help="Version transparente para fondos claros. Si no la subes, Fluxi la genera desde tus imagenes."
                    />
                    <div className="mt-2 flex h-20 items-center justify-center rounded-xl border border-gray-200 bg-white p-3">
                      {previewDarkLogo ? (
                        <img src={previewDarkLogo} alt="Logo negro" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-gray-300" />
                      )}
                    </div>
                    <label className="mt-2 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gray-50 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100">
                      <Upload className="h-3.5 w-3.5" />
                      Subir negro
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => void handleLogoVariantFile("dark", event.target.files?.[0])}
                      />
                    </label>
                  </div>

                  <div>
                    <FieldTitle
                      label="Logo blanco"
                      help="Version transparente para fondos oscuros como Tech Showcase. Si no la subes, Fluxi la genera desde tus imagenes."
                    />
                    <div className="mt-2 flex h-20 items-center justify-center rounded-xl border border-gray-800 bg-gray-950 p-3">
                      {previewLightLogo ? (
                        <img src={previewLightLogo} alt="Logo blanco" className="max-h-full max-w-full object-contain" />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    <label className="mt-2 inline-flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-gray-50 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100">
                      <Upload className="h-3.5 w-3.5" />
                      Subir blanco
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => void handleLogoVariantFile("light", event.target.files?.[0])}
                      />
                    </label>
                  </div>
                </div>
              </aside>

              <div className="space-y-5">
                <section className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1.5 sm:col-span-2">
                    <FieldTitle label="Nombre" help="Opcional al crear con IA. Si lo dejas vacio, Fluxi propone un nombre desde las imagenes y la descripcion." />
                    <input
                      value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                      placeholder="Opcional: Fluxi puede proponerlo"
                      className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                    />
                  </label>
                  <label className="space-y-1.5 sm:col-span-2">
                    <FieldTitle
                      label="Imagenes de marca"
                      help="Fotos del producto, empaque, logo o referencias visuales. Al crear una marca nueva, la IA usa estas imagenes como fuente principal."
                    />
                    <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-3">
                      <p className="mb-3 text-sm text-gray-500">
                        Estas imagenes son el input principal. La referencia de brandboard solo se usa por debajo como composicion.
                      </p>
                      <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl bg-white px-3 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100">
                        <Upload className="h-4 w-4" />
                        Subir imagenes
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(event) => void handleSourceFiles(event.target.files)}
                        />
                      </label>
                      {form.source_images.length > 0 && (
                        <div className="mt-3 grid grid-cols-5 gap-2">
                          {form.source_images.map((image, index) => (
                            <img
                              key={`${image.slice(0, 32)}-${index}`}
                              src={image}
                              alt=""
                              className="aspect-square rounded-lg border border-gray-200 bg-white object-cover"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                </section>

                <section className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-950">Tonos de marca</h3>
                    <p className="text-sm text-gray-500">Mismo contrato de color que se extrae de productos.</p>
                  </div>
                  <div>
                    <p className="mb-1.5 text-xs font-medium text-gray-500">{copy.paletteSourceTitle[language]}</p>
                    <div className="inline-flex rounded-xl bg-gray-100 p-1">
                      {paletteSourceOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setForm({ ...form, palette_source: option.value })}
                          className={cn(
                            "rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors",
                            form.palette_source === option.value
                              ? "bg-white text-gray-950 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          )}
                        >
                          {copy[option.labelKey][language]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {colorFields.map((field) => (
                      <label key={field.key} className="rounded-xl border border-gray-200 p-3">
                        <FieldTitle label={field.label} help={field.help} />
                        <div className="mt-2 flex h-10 items-center gap-2">
                          <input
                            type="color"
                            value={form[field.key]}
                            onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                            className="h-8 w-8 shrink-0 border-0 bg-transparent p-0"
                          />
                          <input
                            value={form[field.key]}
                            onChange={(event) =>
                              setForm({ ...form, [field.key]: cleanColor(event.target.value, form[field.key]) })
                            }
                            className="h-9 min-w-0 flex-1 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-gray-400"
                          />
                        </div>
                      </label>
                    ))}
                  </div>
                </section>

                <section className="grid gap-3 sm:grid-cols-2">
                  <label className="space-y-1.5">
                    <FieldTitle label="Fuente" help="Familia tipografica o nombre de fuente sugerido para la marca." />
                    <input
                      value={form.font_family}
                      onChange={(event) => setForm({ ...form, font_family: event.target.value })}
                      placeholder="Ej. Poppins, Lato"
                      className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                    />
                  </label>
                  <label className="space-y-1.5">
                    <FieldTitle
                      label="Descripcion tipografica"
                      help="Direccion para la IA sobre peso, forma, contraste y sensacion de la tipografia."
                    />
                    <input
                      value={form.font_technical_description}
                      onChange={(event) => setForm({ ...form, font_technical_description: event.target.value })}
                      placeholder="Sans serif moderna, geometrica, amable..."
                      className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                    />
                  </label>
                  <label className="space-y-1.5 sm:col-span-2">
                    <FieldTitle
                      label="Descripcion para crear la marca"
                      help="Describe esencia, mercado, tono o detalles importantes. La IA lo combina con las imagenes para generar la marca."
                    />
                    <textarea
                      value={form.personality_text}
                      onChange={(event) => setForm({ ...form, personality_text: event.target.value })}
                      placeholder="Ej. Marca limpia, directa, premium, enfocada en..."
                      rows={4}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                    />
                  </label>
                </section>

                <details className="rounded-xl border border-gray-200 p-3">
                  <summary className="cursor-pointer text-sm font-semibold text-gray-800">Avanzado</summary>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="space-y-1.5">
                      <FieldTitle label="URL de logo" help="Alternativa tecnica al upload cuando el logo ya vive en una URL publica." />
                      <input
                        value={form.logo_url}
                        onChange={(event) => setForm({ ...form, logo_url: event.target.value, logo_image: "" })}
                        placeholder="https://..."
                        className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                      />
                    </label>
                    <label className="space-y-1.5">
                      <FieldTitle
                        label="URL de brandboard"
                        help="Alternativa tecnica al upload cuando el brandboard ya vive en una URL publica."
                      />
                      <input
                        value={form.brandboard_url}
                        onChange={(event) => setForm({ ...form, brandboard_url: event.target.value, brandboard_image: "" })}
                        placeholder="https://..."
                        className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                      />
                    </label>
                    <label className="space-y-1.5">
                      <FieldTitle label="URL de logo negro" help="Version transparente para fondos claros." />
                      <input
                        value={form.logo_dark_url}
                        onChange={(event) => setForm({ ...form, logo_dark_url: event.target.value, logo_dark_image: "" })}
                        placeholder="https://..."
                        className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                      />
                    </label>
                    <label className="space-y-1.5">
                      <FieldTitle label="URL de logo blanco" help="Version transparente para fondos oscuros." />
                      <input
                        value={form.logo_light_url}
                        onChange={(event) => setForm({ ...form, logo_light_url: event.target.value, logo_light_image: "" })}
                        placeholder="https://..."
                        className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-gray-400"
                      />
                    </label>
                  </div>
                </details>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              {form.id ? (
                <button
                  type="button"
                  onClick={() => void handleDelete({ id: form.id!, name: form.name })}
                  disabled={saving}
                  className="inline-flex h-10 items-center gap-2 rounded-xl px-3 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving || !canSaveForm}
                className={cn(
                  "inline-flex h-10 items-center gap-2 rounded-xl bg-gray-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800",
                  (saving || !canSaveForm) && "cursor-not-allowed opacity-60"
                )}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving && !form.id ? "Creando marca con IA..." : copy.save[language]}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
